import { useCallback, useEffect, useState } from 'react'

/**
 * "Yeni" sidebar rozetlerinin görünürlüğü — kullanıcı tercihi (#770).
 *
 * Backend'de bu tercihi tutan mevcut bir kullanıcı-ayar API'si yok (Ayarlar
 * sayfasındaki diğer tercihler — bildirim, tema ölçeği — de zaten yalnızca
 * localStorage/in-memory'de tutuluyor, bkz. use-display-scale.ts,
 * notification-seen-store.ts). Yeni bir backend endpoint'i açmak bu işin
 * kapsamı dışında; bu yüzden aynı localStorage deseni izlenir.
 *
 * Aynı-sekme senkronizasyonu: custom `EVENT_NAME` event'i (birden çok
 * bileşen aynı anda mount'lu olabilir — sidebar + ayarlar sayfası).
 * Sekmeler-arası senkronizasyon: native `storage` event'i (localStorage
 * başka bir sekmede değiştiğinde otomatik tetiklenir).
 */
const STORAGE_KEY = 'sensei.hideNewBadges'
const EVENT_NAME = 'sensei:new-badges-changed'

function readHidden(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    // SSR / erişim hatası → varsayılan: rozetler AÇIK (hidden=false)
    return false
  }
}

function writeHidden(hidden: boolean): void {
  try {
    if (hidden) {
      localStorage.setItem(STORAGE_KEY, '1')
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // quota aşımı / SSR → sessizce yut, state yine güncellenir (bu sekme için)
  }
}

export function useNewBadgesEnabled(): {
  enabled: boolean
  setEnabled: (enabled: boolean) => void
} {
  const [hidden, setHidden] = useState<boolean>(() => readHidden())

  useEffect(() => {
    const syncFromStorage = () => setHidden(readHidden())

    const handleStorage = (event: StorageEvent) => {
      // key === null → localStorage.clear() çağrıldı, yine de resenkronize et
      if (event.key === STORAGE_KEY || event.key === null) {
        syncFromStorage()
      }
    }

    window.addEventListener(EVENT_NAME, syncFromStorage)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(EVENT_NAME, syncFromStorage)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const setEnabled = useCallback((enabled: boolean) => {
    const nextHidden = !enabled
    writeHidden(nextHidden)
    setHidden(nextHidden)
    try {
      window.dispatchEvent(new Event(EVENT_NAME))
    } catch {
      // ignore
    }
  }, [])

  return { enabled: !hidden, setEnabled }
}
